import React, { useEffect, useMemo } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSelectField from "../components/common/FormSelectField";
import FormSwitchField from "../components/common/FormSwitchField";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createPaymentMethod,
  updatePaymentMethod,
} from "../redux/actions/paymentMethodActions";
import { getAccounts } from "../redux/actions/accountActions";
import { cleanFormData } from "../helpers/formHelpers";
import { paymentMethodActions } from "../redux/slices/paymentMethod";

import { PaymentMethodType } from "../shared/enums/PaymentMethodType";
import { PaymentMethodRequestDto } from "../shared/dtos/paymentMethod/PaymentMethodRequestDto";
import { PaymentMethodResponseDto } from "../shared/dtos/paymentMethod/PaymentMethodResponseDto";

const PAYMENT_TYPES = [
  { value: PaymentMethodType.CASH, label: "Cash" },
  { value: PaymentMethodType.MOBILE_MONEY, label: "Mobile Money" },
  { value: PaymentMethodType.BANK_TRANSFER, label: "Bank Transfer" },
  { value: PaymentMethodType.CHEQUE, label: "Cheque" },
  { value: PaymentMethodType.OTHER, label: "Other" },
];

const paymentMethodSchema = Yup.object().shape({
  name: Yup.string().required("Payment method name is required"),
  type: Yup.string()
    .oneOf(Object.values(PaymentMethodType))
    .required("Type is required"),
  accountId: Yup.string().required("Linked Financial Account is required"),
  active: Yup.boolean(),
});

interface CreateOrUpdatePaymentMethodFormProps {
  paymentMethod?: PaymentMethodResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdatePaymentMethodForm: React.FC<
  CreateOrUpdatePaymentMethodFormProps
> = ({
  paymentMethod = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const { items: accounts } = useAppSelector((state) => state.account);

  useEffect(() => {
    if (!accounts || accounts.length === 0)
      dispatch(getAccounts({ size: 1000 }));
  }, [dispatch, accounts]);

  const accountOptions = useMemo(
    () =>
      accounts?.map((acc) => ({
        value: acc.id,
        label: `${acc.name} (${acc.accountCode})`,
      })) || [],
    [accounts],
  );

  const initialValues: Partial<PaymentMethodRequestDto> = {
    name: paymentMethod?.name || "",
    type: paymentMethod?.type || ("" as PaymentMethodType),
    accountId: paymentMethod?.accountId || "",
    active: paymentMethod?.active ?? true,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? paymentMethodActions.setErrorUpdate(null)
              : paymentMethodActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={paymentMethodSchema}
        onSubmit={(values) => {
          const payload = cleanFormData(
            values,
          ) as unknown as PaymentMethodRequestDto;
          if (isEditing && paymentMethod) {
            dispatch(
              updatePaymentMethod({ id: paymentMethod.id, values: payload }),
            );
          } else {
            dispatch(createPaymentMethod(payload));
          }
        }}
      >
        {({
          dirty,
          isValid,
        }: FormikProps<Partial<PaymentMethodRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField
                  name="name"
                  label="Method Name (e.g. M-Pesa Till)"
                />
                <FormSelectField
                  name="type"
                  label="Type"
                  options={PAYMENT_TYPES}
                />
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  alignItems: "center",
                }}
              >
                <FormSelectField
                  name="accountId"
                  label="Linked General Ledger Account"
                  options={accountOptions}
                  //loading={loadingAccounts}
                />
                <Box
                  sx={{ display: "flex", alignItems: "center", height: "100%" }}
                >
                  <FormSwitchField name="active" label="Active" />
                </Box>
              </Box>

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Method"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdatePaymentMethodForm;
